import { useCallback, useState } from 'react'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps } from 'antd'
import cl from 'classnames'
import { Moment } from 'moment'

import { RangePicker } from 'Shared/atoms'
import { RangePickerPropsI } from 'Shared/atoms/RangePicker/RangePicker'
import { hasError } from 'Shared/utils/validation'

import { FieldNameRegexType } from 'Core/interfaces'

import FormItem from './FormItem'
import { CalendarCb, CalendarType } from './types'

import inputStyles from './input.module.scss'
import styles from './rangePicker.module.scss'

export type FormRangePickerProps<FieldsType extends FieldValues> = RangePickerPropsI &
	FormItemProps & {
		wrapperClassName?: string
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		placeholder?: [string, string]
		disabledDateCallback?: (d: Moment, dates: [Moment | null, Moment | null]) => boolean
	}

function FormRangePicker<FieldsType extends FieldValues>({
	label,
	wrapperClassName,
	control,
	fieldName,
	placeholder = ['Start date', 'End date'],
	disabledDateCallback,
	...props
}: FormRangePickerProps<FieldsType>) {
	const [selectedDates, setSelectedDates] = useState<CalendarType>([null, null])

	const resetState = () => setSelectedDates([null, null])

	const onOpenChange = useCallback(
		(open: boolean) => {
			if (!open && selectedDates.includes(null)) {
				resetState()
			}
		},
		[selectedDates]
	)

	const onCalendarChange = useCallback((dates: CalendarCb) => {
		if (dates !== null && Boolean(dates?.length)) {
			setSelectedDates(dates)
		}

		if (!dates) {
			resetState()
		}
	}, [])

	const disabledDate = useCallback(
		(currentDate: Moment): boolean => {
			if (!disabledDateCallback) {
				return false
			}
			return disabledDateCallback(currentDate, selectedDates)
		},
		[disabledDateCallback, selectedDates]
	)

	return (
		<Controller
			name={fieldName as FieldPath<FieldsType>}
			control={control}
			render={({ field: { name, value, onBlur, ...fieldProps }, fieldState: { error } }) => {
				const isSelectedDates = selectedDates.every((date: Moment | null) => date)

				if (!value && isSelectedDates) {
					resetState()
					return <></>
				}

				return (
					<FormItem
						className={cl(inputStyles.inputWrapper, wrapperClassName)}
						label={label}
						name={name}
						error={hasError(error)}
					>
						<RangePicker
							{...fieldProps}
							placeholder={placeholder}
							separator={<span className={styles.separator}>{'—'}</span>}
							onCalendarChange={onCalendarChange}
							disabledDate={disabledDate}
							onOpenChange={onOpenChange}
							{...props}
						/>
					</FormItem>
				)
			}}
		/>
	)
}

export default FormRangePicker
